import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePerformGlobalRefresh } from "@/components/header/hooks";
import ViewProfileSnackbarAction from "@/components/ViewProfileSnackbarAction";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useWeekIndex } from "@/contexts/WeekIndexProvider";
import { useRosterNurseName } from "@/hooks/rosterCache";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { resolveEffectiveWeekStartDay } from "@/pages/MasterSchedule/v2/resolveEffectiveWeekStartDay";
import { backendService } from "@/services/backend.service";
import type { UpdateMultipleRecurringShiftRequest } from "@/utils/models";

/**
 * The week start the panel lays its template weeks out on. It reads the user's saved view week start —
 * the same value the master schedule, profile and staffing grids render with — so the panel matches
 * the screen it was opened from. The department's `week_start_day` is the payroll week and must not
 * drive this: using it made the panel's weeks differ by entry point for the same nurse.
 */
export const useRecurringShiftPanelWeekStartDay = (): number => {
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const { viewStartDay } = useWeekIndex();
	return resolveEffectiveWeekStartDay(isRuntimeWeekStartEnabled, viewStartDay);
};

export type CreateOrUpdateMultipleRecurringShiftsVariables = UpdateMultipleRecurringShiftRequest & {
	nurseId?: number;
};

export function useCreateOrUpdateMultipleRecurringShifts() {
	const { track, trackError } = useAnalytics();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const rosterNurseName = useRosterNurseName();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async ({ nurseId: _nurseId, ...request }: CreateOrUpdateMultipleRecurringShiftsVariables) =>
			await backendService.createOrUpdateMultipleRecurringShifts(request),
		onSuccess: async (_response, variables) => {
			await performGlobalRefresh({
				avoidRefreshFor: {
					nursesData: true,
					requestsData: true,
					notificationsData: true,
				},
			});
			const nurseId = variables.nurseId;
			const nurseName = nurseId ? rosterNurseName(chosenDepartment.department_id, nurseId) : undefined;
			showSnackbar(
				nurseName ? t("recurring.shifts.updated.successfully.named", { nurseName }) : t("recurring.shifts.updated.successfully"),
				"success",
				undefined,
				nurseName && nurseId ? <ViewProfileSnackbarAction to={`/${chosenDepartment.department_id}/nurse/${nurseId}`} /> : undefined,
			);
			const { nurseId: _nurseId, ...restRequest } = variables;
			track("recurring_shifts_created_or_updated_success", {
				source: "master_schedule",
				request: JSON.stringify(restRequest),
			});
		},
		onError: (error, request) => {
			showErrorSnackbar("Failed to update recurring shifts", request, error);
			trackError("recurring_shifts_create_or_update_fail", {
				source: "master_schedule",
				request: JSON.stringify(request),
			});
		},
	});
}
